import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { FaCheck, FaChevronDown, FaShieldAlt, FaTimes } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import apiClient from '../utils/apiClient';
import LoadingSpinner from '../components/blocks/LoadingSpinner';
import ProgrammeSearch from '../components/widgets/ProgrammeSearch';
import { getProgrammePrimaryTitle, getProgrammeRankingBarTitle } from '../utils/programmeDisplay';
import { getInstitutionShortLabel } from '../utils/institutionAssets';
import { useAuth } from '../contexts/AuthContext';
import { buildScoreCalculationDetailRows } from '../utils/jupasProgrammeDisplay';

function formatScore(value) {
  if (value === null || value === undefined) return '—';
  return Number(value).toLocaleString(undefined, { maximumFractionDigits: 2 });
}

function truncateText(value, maxLength) {
  const text = String(value || '').trim();
  return text.length > maxLength ? `${text.slice(0, maxLength - 1).trimEnd()}…` : text;
}

function InstitutionNavigator({ programmes, activeCode, onSearchStateChange }) {
  const { t, i18n } = useTranslation();
  const institutions = useMemo(() => {
    const firstProgrammeByInstitution = new Map();
    programmes.forEach((programme) => {
      if (!firstProgrammeByInstitution.has(programme.institution)) {
        firstProgrammeByInstitution.set(programme.institution, programme.code);
      }
    });
    return [...firstProgrammeByInstitution.entries()].map(([institution, firstCode]) => ({
      institution,
      firstCode,
      label: getInstitutionShortLabel(institution, i18n.language),
    }));
  }, [i18n.language, programmes]);
  const activeInstitution = programmes.find((programme) => programme.code === activeCode)?.institution;
  const availableCodes = useMemo(
    () => new Set(programmes.map((programme) => programme.code)),
    [programmes]
  );

  const goToInstitution = (firstCode) => {
    document.getElementById(`programme-${firstCode}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <nav
      aria-label={t('jupasPublicResults.institutionNavigatorLabel')}
      className="sticky top-16 z-20 border-y border-edge bg-surface/95 shadow-sm backdrop-blur"
    >
      <div className="mx-auto flex max-w-7xl flex-col gap-2 px-4 py-2 sm:px-6 lg:px-8">
        <div className="flex min-w-0 flex-1 items-center gap-3">
          <span className="hidden shrink-0 text-xs font-bold uppercase tracking-[0.18em] text-ink-subtle sm:block">
            {t('jupasPublicResults.institutionNavigator')}
          </span>
          <div className="flex min-w-0 flex-1 gap-2 overflow-x-auto pb-1 [scrollbar-width:thin]">
            {institutions.map(({ institution, firstCode, label }) => (
              <button
                key={institution}
                type="button"
                onClick={() => goToInstitution(firstCode)}
                aria-current={activeInstitution === institution ? 'location' : undefined}
                className={`shrink-0 rounded-full border px-3 py-1.5 text-sm font-bold tabular-nums transition focus:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                  activeInstitution === institution
                    ? 'border-primary bg-primary text-primary-foreground shadow-sm'
                    : 'border-edge-strong bg-surface text-ink-muted hover:border-primary hover:text-primary'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
        <div className="relative z-10 w-full">
          <ProgrammeSearch
            selectedCollection="jupas_programme_info_2026"
            includeFilters={false}
            inlineResults
            filterProgramme={(programme) => availableCodes.has(programme.code)}
            onSearchStateChange={onSearchStateChange}
          />
        </div>
      </div>
    </nav>
  );
}

function ApplicantScoreDetails({ applicant, programmeCode }) {
  const { t } = useTranslation();
  const triggerRef = useRef(null);
  const [popover, setPopover] = useState(null);
  const rows = useMemo(
    () => buildScoreCalculationDetailRows(applicant.calculationBreakdown, t),
    [applicant.calculationBreakdown, t]
  );
  const popoverId = `applicant-score-detail-${programmeCode}-${applicant.scoreRank}`;

  const showDetails = () => {
    if (!rows.length || !triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const width = 256;
    const estimatedHeight = Math.min(230, 58 + (rows.length + 1) * 29);
    const placeAbove = rect.bottom + estimatedHeight + 12 > window.innerHeight;
    setPopover({
      left: Math.max(8, Math.min(window.innerWidth - width - 8, rect.left + (rect.width - width) / 2)),
      top: placeAbove ? rect.top - 7 : rect.bottom + 7,
      placeAbove,
    });
  };

  if (!rows.length) {
    return (
      <p className="text-[1.3rem] font-black leading-none tabular-nums text-ink">
        {formatScore(applicant.weightedScore)}
      </p>
    );
  }

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onMouseEnter={showDetails}
        onMouseLeave={() => setPopover(null)}
        onFocus={showDetails}
        onBlur={() => setPopover(null)}
        onClick={() => (popover ? setPopover(null) : showDetails())}
        aria-label={`${t('jupasPublicResults.weightedScore')}: ${formatScore(applicant.weightedScore)}. ${t('jupa.scoreCalculationDetailHeading')}`}
        aria-describedby={popover ? popoverId : undefined}
        aria-expanded={Boolean(popover)}
        className="inline-flex items-center gap-1 rounded text-[1.3rem] font-black leading-none tabular-nums text-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        {formatScore(applicant.weightedScore)}
        <FaChevronDown className={`h-2.5 w-2.5 text-ink-subtle transition-transform ${popover ? 'rotate-180' : ''}`} aria-hidden />
      </button>

      {popover ? createPortal(
        <div
          id={popoverId}
          role="tooltip"
          className="fixed z-[100] w-64 overflow-hidden rounded-lg border border-edge-strong bg-surface text-left shadow-xl"
          style={{
            left: popover.left,
            top: popover.top,
            transform: popover.placeAbove ? 'translateY(-100%)' : undefined,
          }}
        >
          <div className="border-b border-edge bg-surface-muted px-2.5 py-1.5 text-[11px] font-bold text-ink">
            {t('jupa.scoreCalculationDetailHeading')}
          </div>
          <div className="grid grid-cols-[minmax(0,1fr)_2.2rem_2.7rem_2.7rem] gap-x-1 border-b border-edge px-2 py-1 text-[9px] font-semibold text-ink-subtle">
            <span>{t('jupa.scoreCalcDetail.subject')}</span>
            <span className="text-center">{t('jupa.scoreCalcDetail.grade')}</span>
            <span className="text-center">{t('jupa.scoreCalcDetail.multiplier')}</span>
            <span className="text-right">{t('jupa.scoreCalcDetail.score')}</span>
          </div>
          <div className="max-h-44 overflow-y-auto">
            {rows.map((row) => (
              <div key={row.key} className="grid grid-cols-[minmax(0,1fr)_2.2rem_2.7rem_2.7rem] gap-x-1 border-b border-edge/70 px-2 py-1 text-[10px] leading-4 text-ink last:border-b-0">
                <span className="truncate font-medium" title={row.subjectLabel}>{row.subjectLabel}</span>
                <span className="text-center tabular-nums">{row.grade}</span>
                <span className="text-center tabular-nums text-ink-muted">{row.multiplier}</span>
                <span className="text-right font-semibold tabular-nums">{row.score}</span>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-[minmax(0,1fr)_2.2rem_2.7rem_2.7rem] gap-x-1 border-t border-edge-strong bg-surface-muted px-2 py-1.5 text-[10px] font-bold text-ink">
            <span>{t('jupa.scoreCalcDetail.total')}</span>
            <span />
            <span />
            <span className="text-right tabular-nums">{formatScore(applicant.weightedScore)}</span>
          </div>
        </div>,
        document.body
      ) : null}
    </>
  );
}

function ApplicantTable({ programme }) {
  const { t } = useTranslation();

  return (
    <div className="h-[80px] min-w-0 overflow-x-auto overflow-y-hidden rounded-lg border border-edge bg-surface">
        <table className="h-full w-full min-w-[40rem] table-fixed border-collapse">
          <caption className="sr-only">
            {t('jupasPublicResults.tableCaption', { code: programme.code })}
          </caption>
          <tbody>
            <tr>
              {programme.applicants.map((applicant) => {
                const admitted = applicant.status === 'admitted';
                return (
                  <td
                    key={applicant.applicant}
                    className="border-r border-edge p-0 align-top last:border-r-0"
                  >
                    <article className={`flex h-[78px] flex-col border-t-2 px-2.5 py-1.5 ${admitted ? 'border-success' : 'border-danger'}`}>
                      <div className="flex items-center justify-between gap-1 text-[11px] leading-none">
                        <span className="truncate font-semibold text-ink-subtle">
                          {t('jupasPublicResults.applicant', { number: applicant.scoreRank })}
                        </span>
                        <span
                          className="shrink-0 rounded bg-surface-muted px-1 py-0.5 font-bold tabular-nums text-ink"
                          aria-label={`${t('jupasPublicResults.ranking')}: ${applicant.jupasRanking || '—'}`}
                        >
                          {applicant.jupasRanking || '—'}
                        </span>
                      </div>
                      <div className="mt-0.5 text-center">
                        <ApplicantScoreDetails applicant={applicant} programmeCode={programme.code} />
                      </div>
                      <div className="mt-auto text-center">
                        <span className={`inline-flex max-w-full items-center gap-1 rounded px-1.5 py-1 text-[11px] font-bold leading-none ${
                          admitted
                            ? 'bg-success/10 text-success'
                            : 'bg-danger/10 text-danger'
                        }`}>
                          {admitted ? <FaCheck className="h-2.5 w-2.5" aria-hidden /> : <FaTimes className="h-2.5 w-2.5" aria-hidden />}
                          {t(admitted ? 'jupasPublicResults.admitted' : 'jupasPublicResults.notAdmitted')}
                        </span>
                      </div>
                    </article>
                  </td>
                );
              })}
              {Array.from({ length: Math.max(0, 6 - programme.applicants.length) }, (_, index) => (
                <td key={`empty-${index}`} className="border-r border-edge bg-surface-muted/20 last:border-r-0" aria-hidden="true" />
              ))}
            </tr>
          </tbody>
        </table>
    </div>
  );
}

function PersonalScoreBlock({ loading, hasScores, score }) {
  const { t } = useTranslation();

  if (loading) {
    return (
      <aside className="flex h-[80px] items-center justify-center rounded-lg border border-edge bg-surface" aria-label={t('jupasPublicResults.yourScore')}>
        <LoadingSpinner size="small" />
      </aside>
    );
  }

  if (!hasScores) {
    return (
      <Link
        to="/jupas-calculator"
        className="group relative flex h-[80px] items-center justify-center rounded-lg border border-amber-300 bg-amber-50 text-amber-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring dark:border-amber-700/70 dark:bg-amber-950/40 dark:text-amber-300"
        aria-label={t('jupasPublicResults.enterScoresPrompt')}
      >
        <span className="flex h-9 w-9 items-center justify-center rounded-full bg-amber-200 text-xl font-black dark:bg-amber-800/70" aria-hidden>!</span>
        <span className="pointer-events-none absolute left-1/2 top-full z-30 mt-2 hidden w-56 -translate-x-1/2 rounded-lg bg-ink px-3 py-2 text-center text-xs font-medium leading-5 text-ink-inverse shadow-lg group-hover:block group-focus-visible:block">
          {t('jupasPublicResults.enterScoresPrompt')}
        </span>
      </Link>
    );
  }

  return (
    <aside className="flex h-[80px] min-w-0 flex-col items-center justify-center rounded-lg border border-edge bg-surface px-2" aria-label={t('jupasPublicResults.yourScore')}>
      <p className="text-xs font-semibold text-ink-subtle">{t('jupasPublicResults.yourScore')}</p>
      <p className="mt-1 text-xl font-black leading-none tabular-nums text-primary">{formatScore(score)}</p>
    </aside>
  );
}

function ProgrammeSection({ programme, language, showPersonalScore, personalScoreState }) {
  const { t } = useTranslation();
  const displayProgramme = {
    code: programme.code,
    short_name: programme.shortName,
    full_title_en: programme.titleEn,
    full_title_cn: programme.titleCn,
  };
  const title = getProgrammeRankingBarTitle(displayProgramme, language);
  const fullTitle = getProgrammePrimaryTitle(displayProgramme, language);
  const institutionLabel = getInstitutionShortLabel(programme.institution, language);
  const references = [
    ['upperQuartile', 'UQ'],
    ['median', 'M'],
    ['lowerQuartile', 'LQ'],
  ];
  const gridClass = showPersonalScore
    ? 'md:grid-cols-[minmax(0,1.25fr)_minmax(7rem,0.45fr)_minmax(10rem,0.65fr)] lg:grid-cols-[minmax(12rem,0.85fr)_minmax(7rem,0.45fr)_minmax(10rem,0.65fr)_minmax(0,3.15fr)]'
    : 'md:grid-cols-[minmax(0,1.35fr)_minmax(10rem,0.65fr)] lg:grid-cols-[minmax(13rem,0.9fr)_minmax(10rem,0.65fr)_minmax(0,3.2fr)]';

  return (
    <section id={`programme-${programme.code}`} className="scroll-mt-32 py-1.5">
      <div className={`grid gap-2 lg:items-stretch ${gridClass}`}>
        <article className="flex h-[80px] min-w-0 flex-col justify-center overflow-hidden rounded-lg border border-edge bg-surface px-3 py-2">
          <div className="flex min-w-0 items-center gap-2">
            <span className="shrink-0 rounded border border-sky-100 bg-sky-50 px-1.5 py-1 text-[11px] font-bold leading-none text-[#185FA5] dark:border-sky-800/60 dark:bg-sky-950/50 dark:text-sky-300">
              {programme.code}
            </span>
            <span className="truncate text-xs text-ink-muted" title={institutionLabel}>
              {institutionLabel}
            </span>
          </div>
          <h2 className="mt-1.5 truncate text-sm font-semibold leading-5 text-ink" title={fullTitle}>
            {truncateText(title, 52)}
          </h2>
        </article>

        {showPersonalScore ? (
          <PersonalScoreBlock
            loading={personalScoreState.loading}
            hasScores={personalScoreState.hasScores}
            score={personalScoreState.scoresByCode[programme.code]}
          />
        ) : null}

        <aside className="flex h-[80px] min-w-0 flex-col justify-center rounded-lg border border-edge bg-surface px-3 py-1.5" aria-label={t('jupasPublicResults.pastScoresShort')}>
          <p className="truncate text-center text-xs font-semibold text-ink-subtle">
            {t('jupasPublicResults.pastScoresShort')}
          </p>
          <dl className="mt-1.5 grid grid-cols-3 divide-x divide-edge">
            {references.map(([key, label]) => (
              <div key={key} className="px-1 text-center">
                <dt className="text-[11px] font-semibold text-ink-subtle">{label}</dt>
                <dd className="mt-0.5 text-base font-bold leading-none tabular-nums text-ink">{formatScore(programme.referenceScores?.[key])}</dd>
              </div>
            ))}
          </dl>
        </aside>

        <div className={showPersonalScore ? 'md:col-span-3 lg:col-span-1' : 'md:col-span-2 lg:col-span-1'}>
          <ApplicantTable programme={programme} />
        </div>
      </div>
    </section>
  );
}

export default function JupasPublicResults() {
  const { t, i18n } = useTranslation();
  const { isAuthenticated } = useAuth();
  const [programmes, setProgrammes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeCode, setActiveCode] = useState('');
  const [programmeSearchState, setProgrammeSearchState] = useState({
    query: '',
    results: [],
    loading: false,
    error: null,
  });
  const [personalScoreState, setPersonalScoreState] = useState({
    loading: false,
    hasScores: false,
    scoresByCode: {},
  });
  const programmeCodeKey = useMemo(
    () => programmes.map((programme) => programme.code).join(','),
    [programmes]
  );

  useEffect(() => {
    let active = true;
    async function loadResults() {
      try {
        const response = await apiClient.get('/api/jupas-results');
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || t('jupasPublicResults.loadFailed'));
        if (!active) return;
        setProgrammes(data.programmes || []);
        setActiveCode(data.programmes?.[0]?.code || '');
      } catch (loadError) {
        if (active) setError(loadError.message || t('jupasPublicResults.loadFailed'));
      } finally {
        if (active) setLoading(false);
      }
    }
    loadResults();
    return () => { active = false; };
  }, [t]);

  useEffect(() => {
    let active = true;
    if (!isAuthenticated || !programmeCodeKey) {
      setPersonalScoreState({ loading: false, hasScores: false, scoresByCode: {} });
      return () => { active = false; };
    }

    setPersonalScoreState((current) => ({ ...current, loading: true }));
    apiClient.post('/api/jupas-results/my-scores', {
      programmeCodes: programmeCodeKey.split(','),
    }).then(async (response) => {
      const data = await response.json();
      if (!response.ok || !data.success) throw new Error(data.message || 'Failed to calculate scores');
      if (active) {
        setPersonalScoreState({
          loading: false,
          hasScores: Boolean(data.hasScores),
          scoresByCode: data.scoresByCode || {},
        });
      }
    }).catch(() => {
      if (active) setPersonalScoreState({ loading: false, hasScores: false, scoresByCode: {} });
    });

    return () => { active = false; };
  }, [isAuthenticated, programmeCodeKey]);

  const filteredProgrammes = useMemo(() => {
    if (!programmeSearchState.query.trim()) return programmes;
    const matchingCodes = new Set(programmeSearchState.results.map((programme) => programme.code));
    return programmes.filter((programme) => matchingCodes.has(programme.code));
  }, [programmeSearchState.query, programmeSearchState.results, programmes]);

  useEffect(() => {
    if (!filteredProgrammes.length || !('IntersectionObserver' in window)) return undefined;
    const observer = new IntersectionObserver((entries) => {
      const visible = entries
        .filter((entry) => entry.isIntersecting)
        .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0];
      if (visible) setActiveCode(visible.target.id.replace('programme-', ''));
    }, { rootMargin: '-30% 0px -60% 0px' });
    filteredProgrammes.forEach(({ code }) => {
      const element = document.getElementById(`programme-${code}`);
      if (element) observer.observe(element);
    });
    return () => observer.disconnect();
  }, [filteredProgrammes]);

  useEffect(() => {
    if (filteredProgrammes.length && !filteredProgrammes.some(({ code }) => code === activeCode)) {
      setActiveCode(filteredProgrammes[0].code);
    }
  }, [activeCode, filteredProgrammes]);

  const admittedCount = useMemo(
    () => programmes.reduce((total, programme) => total + programme.applicants.filter((item) => item.status === 'admitted').length, 0),
    [programmes]
  );

  return (
    <div className="min-h-screen bg-canvas">
      <header className="relative overflow-hidden border-b border-edge bg-surface">
        <div className="pointer-events-none absolute -right-24 -top-32 h-80 w-80 rounded-full bg-accent/25 blur-3xl" aria-hidden />
        <div className="pointer-events-none absolute -left-16 bottom-0 h-48 w-48 rounded-full bg-success/10 blur-3xl" aria-hidden />
        <div className="relative mx-auto max-w-7xl px-4 py-12 sm:px-6 sm:py-16 lg:px-8">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 rounded-full border border-edge bg-surface-muted px-3 py-1.5 text-xs font-bold uppercase tracking-[0.16em] text-ink-muted">
              <FaShieldAlt className="text-success" aria-hidden />
              {t('jupasPublicResults.privacyBadge')}
            </div>
            <h1 className="mt-5 text-4xl font-black tracking-tight text-ink sm:text-5xl">
              {t('jupasPublicResults.title')}
            </h1>
            <p className="mt-4 max-w-2xl text-base leading-7 text-ink-muted sm:text-lg">
              {t('jupasPublicResults.subtitle')}
            </p>
            {!loading && !error && programmes.length ? (
              <div className="mt-7 flex flex-wrap gap-3 text-sm">
                <span className="rounded-full bg-primary px-3.5 py-2 font-bold text-primary-foreground">
                  {t('jupasPublicResults.programmeCount', { count: programmes.length })}
                </span>
                <span className="rounded-full border border-edge-strong bg-surface px-3.5 py-2 font-semibold text-ink-muted">
                  {t('jupasPublicResults.admissionCount', { count: admittedCount })}
                </span>
              </div>
            ) : null}
          </div>
        </div>
      </header>

      {programmes.length ? (
        <InstitutionNavigator
          programmes={programmes}
          activeCode={activeCode}
          onSearchStateChange={setProgrammeSearchState}
        />
      ) : null}

      <main className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {loading ? (
          <div className="flex min-h-80 items-center justify-center"><LoadingSpinner size="large" text={t('jupasPublicResults.loading')} /></div>
        ) : error ? (
          <div className="my-12 rounded-2xl border border-danger/30 bg-danger/10 p-6 text-center text-danger">{error}</div>
        ) : programmes.length === 0 ? (
          <div className="my-12 rounded-2xl border border-edge bg-surface p-10 text-center">
            <h2 className="text-xl font-bold text-ink">{t('jupasPublicResults.emptyTitle')}</h2>
            <p className="mt-2 text-ink-muted">{t('jupasPublicResults.emptyBody')}</p>
          </div>
        ) : programmeSearchState.error ? (
          <div className="my-12 rounded-2xl border border-danger/30 bg-danger/10 p-6 text-center text-danger">
            {programmeSearchState.error}
          </div>
        ) : programmeSearchState.query.trim() && !programmeSearchState.loading && filteredProgrammes.length === 0 ? (
          <div className="my-12 rounded-2xl border border-edge bg-surface p-10 text-center text-ink-muted">
            {t('jupa.noProgrammesFoundMatching')} &ldquo;{programmeSearchState.query}&rdquo;
          </div>
        ) : (
          filteredProgrammes.map((programme) => (
            <ProgrammeSection
              key={programme.code}
              programme={programme}
              language={i18n.language}
              showPersonalScore={isAuthenticated}
              personalScoreState={personalScoreState}
            />
          ))
        )}

        {!loading && programmes.length ? (
          <p className="pb-10 pt-2 text-center text-xs leading-5 text-ink-subtle">
            {t('jupasPublicResults.disclaimer')}
          </p>
        ) : null}
      </main>
    </div>
  );
}
